import React, { useEffect, useRef, useState } from "react";
import { IconButton, Typography, Box } from "@mui/material";
import { styled } from "@mui/styles";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gradients,
  iconSize,
  margin,
  padding,
} from "theme/defaultStyle";
import usePolicyStore from "utils/context/InventoryPolicyContext";
import { StewartDialog } from "controls/global/dialogs/stewart-dialog/StewartDialog";
import { FileRejection, FileWithPath } from "react-dropzone";
import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import UploadStatusGrid from "controls/global/upload-files/UploadStatusGrid";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import FlashMessage from "controls/global/flash-message/FlashMessage";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import { useUploadDropZoneActions } from "utils/context/UploadDropZoneContext";
import { useFlashMessageActions } from "utils/context/FlashMessageContext";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import useFlashMessage from "utils/custom-hooks/useFlashMessage";
import {
  ActionResultStatus,
  FlashMessageType,
  ProgressImageType,
  UIConstants,
} from "utils/data/enum";
import { FileUploadAdditionalInfo, InventoryVerifyStatus } from "entities/UIModel";
import {
  getFormattedErrorMessage,
  mapInventoryVerifyResponse,
  mapRejectedFiles,
} from "pages/lookup/inventory-verify/InventoryVerifyHelper";

interface BaseProps {
  open: boolean;
  onClose: () => void;
}

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "95vh",
    maxWidth: "54vw",
    minWidth: "54vw",
    display: "flex",
    justifyContent: "flex-start",
    padding: "36px 40px 40px 40px",
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge,
    fontWeight: fontWeight.bold2,
    color: colors.blue15,
  },
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: "4px",
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const StyledDialogContent = styled(Box)({
  marginTop: margin.large,
  "& .failed-upload-status": {
    marginTop: margin.xlarge1,
    marginBottom: margin.large,
    fontWeight: fontWeight.bold1,
    fontSize: fontSize.large,
  },
});

const REGISTER_UPLOAD_SUCCESS_MESSAGE = "The File is being Processed!";
const REGISTER_UPLOAD_ERROR_MESSAGE_HEADER = "Upload Failed!";
const REGISTER_UPLOAD_ERROR_MESSAGE_DETAIL = "Please try again. ";

const UploadRegistersModal = ({ open, onClose }: BaseProps) => {
  const [, { uploadRegister }] = usePolicyStore();
  const { generalConfig } = useConfigContext();

  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);
  const [showUploadError, setShowUploadError] = useState<boolean>(false);
  const [uploadStatusList, setUploadStatusList] = useState<InventoryVerifyStatus[]>([]);

  const isProgressCompletedRef = useRef<boolean>(false);

  // progress dialog
  const [, { setBypassDefaultLoader, setPercentageComplete }] = useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [runProgress, setRunProgress] = useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = useState<string>("");

  // drop zone + flash message
  const [, { setDisabled, setVisble }] = useUploadDropZoneActions();
  const [, { showFlashMessage, closeFlashMessage }] = useFlashMessageActions();
  useFlashMessage();

  const handleOnCloseProgressDialog = async () => {
    isProgressCompletedRef.current = true;
    if (uploadCompleted) {
      setOpenProgressDialog(false);
      setRunProgress(false);
      setProgressRequestId("");
    }
  };

  const handleOnClose = () => {
    onClose && onClose();
  };

  const upload = async (
    acceptedFiles: FileWithPath[],
    rejectedFiles: FileRejection[]
  ) => {
    // -- Show progress dialog
    setUploadCompleted(false);
    const requestId = uuidv4();
    setProgressRequestId(requestId);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);
    isProgressCompletedRef.current = false;

    // -- Build form data (files + per-file info), mirroring Verify Inventory
    const fileAdditionalInfos: FileUploadAdditionalInfo[] = [];
    const formData = new FormData();

    let index = 0;
    acceptedFiles?.forEach((file) => {
      formData.append("files", file, file.name);
      fileAdditionalInfos.push({
        index,
        filePath: file.path || "",
        fileName: file.name,
        fileSize: file.size,
        errorMessage: "",
        isValidFile: true,
      });
      index++;
    });
    rejectedFiles?.forEach((rejectedFile) => {
      fileAdditionalInfos.push({
        index,
        filePath: "",
        fileName: rejectedFile.file.name,
        fileSize: rejectedFile.file.size,
        errorMessage: getFormattedErrorMessage(rejectedFile.errors),
        isValidFile: false,
      });
      index++;
    });

    formData.append(
      "fileUploadAdditionalInfo",
      JSON.stringify(fileAdditionalInfos)
    );

    // -- Upload
    const result = await uploadRegister(requestId, formData);

    if (result.status === ActionResultStatus.Failed) {
      showFlashMessage(
        FlashMessageType.Error,
        REGISTER_UPLOAD_ERROR_MESSAGE_HEADER,
        REGISTER_UPLOAD_ERROR_MESSAGE_DETAIL
      );
    } else {
      const statusList = mapInventoryVerifyResponse(result.apiResponse);
      const hasUploadError = statusList?.some(
        (s: InventoryVerifyStatus) => s.isUploadSuccess === false
      );

      if (!hasUploadError) {
        showFlashMessage(FlashMessageType.Success, REGISTER_UPLOAD_SUCCESS_MESSAGE, "");
        handleOnClose();
      } else {
        setUploadStatusList((statusList || []).filter((s) => !s.isUploadSuccess));
        setShowUploadError(true);
      }
    }

    setPercentageComplete(requestId);
    setUploadCompleted(true);
  };

  const handleOnUpload = async (
    acceptedFiles: FileWithPath[],
    rejectedFiles: FileRejection[]
  ) => {
    setShowUploadError(false);
    setUploadStatusList([]);

    if (acceptedFiles?.length > 0) {
      setDisabled(true);
      setVisble(false);
      await upload(acceptedFiles, rejectedFiles);
      setDisabled(false);
      setVisble(true);
    } else {
      setUploadStatusList(mapRejectedFiles(rejectedFiles));
      setShowUploadError(true);
    }
  };

  const handleFlashMessageClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    closeFlashMessage();
  };

  useEffect(() => {
    setShowUploadError(false);
    setUploadStatusList([]);
    isProgressCompletedRef.current = false;
  }, [open]);

  useEffect(() => {
    if (uploadCompleted && openProgressDialog) {
      handleOnCloseProgressDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCompleted]);

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle>
          <IconContainer>
            <CloudUploadOutlinedIcon />
          </IconContainer>
          <Typography>Upload Registers</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleOnClose}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>

        <StyledDialogContent>
          <UploadDropZone
            handleOnUpload={handleOnUpload}
            uploadConfiguration={generalConfig?.registerUploadConfiguration}
          />

          {showUploadError && (
            <>
              <div className="failed-upload-status">Failed to Upload</div>
              <UploadStatusGrid
                className={"failed-status-only-grid"}
                fileUploadStatuses={uploadStatusList}
              />
            </>
          )}
        </StyledDialogContent>
      </StyledDialog>

      <ConfirmationDialogWithProgressbar
        autoClose={true}
        title="Please wait while uploading your registers ..."
        closeButtonText="Done! Click to Continue"
        isOpen={openProgressDialog}
        onClose={handleOnCloseProgressDialog}
        requestId={progressRequestId}
        runProgress={runProgress}
        progressImageType={ProgressImageType.circular}
      />

      <FlashMessage
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={Number(UIConstants.FLASH_MESSAGE_AUTO_HIDDEN_DURATION)}
        onClose={handleFlashMessageClose}
      />
    </>
  );
};

export default UploadRegistersModal;
